import { z } from "zod";
import { organizationConnectionPaginationQuerySchema } from "./organization-connection-pagination.schema";
import { organizationConnectionFiltersSchema } from "./organization-connection-filters.schema";
import {
    organizationUnifiedConnectionAccountSchema,
    organizationUnifiedConnectionSchema,
} from "./organization-unified-connection.schema";

/**
 * How the unified connections list is grouped. `login` groups the flattened
 * account list by connection (one bank login); `institution` merges all logins
 * to the same institution; `type` groups by the account's financial type;
 * `client` groups by the client (workspace) an account is assigned to;
 * `connectedBy` groups by the user who connected the account; `status` groups
 * by the row's resolved integration status.
 */
export const organizationConnectionGroupBySchema = z.enum([
    "login",
    "institution",
    "type",
    "client",
    "connectedBy",
    "status",
]);

export type OrganizationConnectionGroupBy = z.infer<
    typeof organizationConnectionGroupBySchema
>;

export const organizationConnectionGroupsQuerySchema =
    organizationConnectionPaginationQuerySchema
        .extend({ groupBy: organizationConnectionGroupBySchema })
        .merge(organizationConnectionFiltersSchema);

export type OrganizationConnectionGroupsQuery = z.infer<
    typeof organizationConnectionGroupsQuerySchema
>;

/**
 * How the account rows inside one group bucket are ordered. Applies to items
 * only — group bucket ordering is fixed per dimension. Absent means the stable
 * default (account id ascending). `createdAt` is the connection's creation
 * date, so accounts of one login sort together.
 */
export const organizationConnectionItemSortSchema = z.enum([
    "accountNameAsc",
    "accountNameDesc",
    "clientNameAsc",
    "clientNameDesc",
    "createdAtAsc",
    "createdAtDesc",
]);

export type OrganizationConnectionItemSort = z.infer<
    typeof organizationConnectionItemSortSchema
>;

export const organizationConnectionGroupItemsQuerySchema =
    organizationConnectionPaginationQuerySchema
        .extend({
            groupBy: organizationConnectionGroupBySchema,
            groupKey: z.string(),
            sort: organizationConnectionItemSortSchema.optional(),
        })
        .merge(organizationConnectionFiltersSchema);

export type OrganizationConnectionGroupItemsQuery = z.infer<
    typeof organizationConnectionGroupItemsQuerySchema
>;

/**
 * One group bucket over the flattened account list. `totalCount` is the number
 * of accounts in the bucket across all pages, not just the current page.
 */
export const organizationConnectionGroupSchema = z.object({
    key: z.string(),
    label: z.string(),
    iconUrl: z.string().nullable(),
    totalCount: z.number(),
});

export type OrganizationConnectionGroup = z.infer<
    typeof organizationConnectionGroupSchema
>;

export const organizationConnectionGroupsResponseSchema = z.object({
    groups: z.array(organizationConnectionGroupSchema),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
    totalAccounts: z.number(),
});

export type OrganizationConnectionGroupsResponse = z.infer<
    typeof organizationConnectionGroupsResponseSchema
>;

/**
 * A single flattened item within a group: one financial account plus its
 * connection back-reference (the unified connection without its accounts, so
 * the item is self-contained but not recursively nested).
 */
export const organizationIntegrationAccountItemSchema = z.object({
    id: z.number(),
    account: organizationUnifiedConnectionAccountSchema,
    connection: organizationUnifiedConnectionSchema.omit({ accounts: true }),
});

export type OrganizationIntegrationAccountItem = z.infer<
    typeof organizationIntegrationAccountItemSchema
>;

export const organizationConnectionGroupItemsResponseSchema = z.object({
    items: z.array(organizationIntegrationAccountItemSchema),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
});

export type OrganizationConnectionGroupItemsResponse = z.infer<
    typeof organizationConnectionGroupItemsResponseSchema
>;
